import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthProvider';
import { useAuth } from './hooks/useAuth';
import { useData } from './hooks/useData';
import { DataProvider } from './context/DataContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';

// Test components
import LoginTest from './components/LoginTest';
import AuthTest from './components/AuthTest';
import AuthTestEnhanced from './components/AuthTestEnhanced';
import Unauthorized from './components/Unauthorized';
import DataContextTest from './components/DataContextTest';

// Chunk loading error handler (hoisted so it can be used by lazy imports)
function retryLazyImport(componentImport) {
  return new Promise((resolve, reject) => {
    componentImport()
      .then(resolve)
      .catch((error) => {
        console.error('Chunk loading error:', error);
        // Retry once after a short delay
        setTimeout(() => {
          componentImport()
            .then(resolve)
            .catch((retryError) => {
              console.error('Retry failed:', retryError);
              // If retry fails, reload the page to get fresh chunks
              window.location.reload();
              reject(retryError);
            });
        }, 1000);
      });
  });
}

// Lazy load components for code splitting
const Header = lazy(() => retryLazyImport(() => import('./components/Header')));
const Sidebar = lazy(() => retryLazyImport(() => import('./components/Sidebar')));

// Test component for environment variables
const EnvTest = lazy(() => retryLazyImport(() => import('./components/EnvTest')));

// Test component for API communication
const ApiTest = lazy(() => retryLazyImport(() => import('./components/ApiTest')));

// Socket.io test component
const SocketTest = lazy(() => retryLazyImport(() => import('./components/SocketTest')));

// User components
const UserDashboard = lazy(() => retryLazyImport(() => import('./components/user/UserDashboard')));
const InvestmentPlans = lazy(() => retryLazyImport(() => import('./components/user/InvestmentPlans')));
const Portfolio = lazy(() => retryLazyImport(() => import('./components/user/Portfolio')));
const Wallet = lazy(() => retryLazyImport(() => import('./components/user/Wallet')));
const KYC = lazy(() => retryLazyImport(() => import('./components/user/KYC')));
const Profile = lazy(() => retryLazyImport(() => import('./components/user/Profile')));
const Referrals = lazy(() => retryLazyImport(() => import('./components/user/Referrals')));
const Support = lazy(() => retryLazyImport(() => import('./components/user/Support')));
const BankDetails = lazy(() => retryLazyImport(() => import('./components/user/BankDetails')));
const NotificationCenter = lazy(() => retryLazyImport(() => import('./components/user/NotificationCenter')));
const TransactionHistory = lazy(() => retryLazyImport(() => import('./components/user/TransactionHistory')));
const Withdrawal = lazy(() => retryLazyImport(() => import('./components/user/Withdrawal')));

// Admin components
const AdminDashboard = lazy(() => retryLazyImport(() => import('./components/admin/AdminDashboard')));
const AdminUsers = lazy(() => retryLazyImport(() => import('./components/admin/AdminUsers')));
const AdminInvestments = lazy(() => retryLazyImport(() => import('./components/admin/AdminInvestments')));
const AdminInvestmentPlansManagement = lazy(() => retryLazyImport(() => import('./components/admin/AdminInvestmentPlansManagement')));
const AdminTransactions = lazy(() => retryLazyImport(() => import('./components/admin/AdminTransactions')));
const AdminWithdrawals = lazy(() => retryLazyImport(() => import('./components/admin/AdminWithdrawals')));
const AdminReports = lazy(() => retryLazyImport(() => import('./components/admin/AdminReports')));
const AdminSettings = lazy(() => retryLazyImport(() => import('./components/admin/AdminSettings')));
const AdminKYC = lazy(() => retryLazyImport(() => import('./components/admin/AdminKYC')));
const AdminROI = lazy(() => retryLazyImport(() => import('./components/admin/AdminROI')));
const AdminSettlements = lazy(() => retryLazyImport(() => import('./components/admin/AdminSettlements')));
const AdminReferrals = lazy(() => retryLazyImport(() => import('./components/admin/AdminReferrals')));
const AdminBankDetails = lazy(() => retryLazyImport(() => import('./components/admin/AdminBankDetails')));
const AdminNotifications = lazy(() => retryLazyImport(() => import('./components/admin/AdminNotifications')));
const AdminProfile = lazy(() => retryLazyImport(() => import('./components/admin/AdminProfile')));
const PaymentManagement = lazy(() => retryLazyImport(() => import('./components/admin/PaymentManagement')));
const AdminGoldRates = lazy(() => retryLazyImport(() => import('./components/admin/AdminGoldRates')));

// Auth components
const Login = lazy(() => retryLazyImport(() => import('./components/auth/Login')));
const EnhancedLogin = lazy(() => retryLazyImport(() => import('./components/auth/EnhancedLogin')));
const Register = lazy(() => retryLazyImport(() => import('./components/auth/Register')));

// UI components
const ToastContainer = lazy(() => retryLazyImport(() => import('./components/ui/ToastContainer')));
const RealTimeNotifications = lazy(() => retryLazyImport(() => import('./components/ui/RealTimeNotifications')));

// Loading component
const LoadingSpinner = () => {
  const { backendStatus, retryBackendCheck } = useData();
  const isOnline = backendStatus?.state === 'online';
  const isOffline = backendStatus?.state === 'offline';
  const label = 'SS';
  // Only show backend status message if backend is actually offline
  const message = isOffline ? (backendStatus?.message || 'Connecting...') : 'Loading...';
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-yellow-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-2xl mx-auto flex items-center justify-center mb-4 animate-pulse">
          <span className="text-white font-bold text-2xl">{label}</span>
        </div>
        <p className="text-gray-700">{message}</p>
        {backendStatus?.state === 'offline' && (
          <div className="mt-3">
            <button
              onClick={retryBackendCheck}
              className="px-3 py-1 rounded-md bg-yellow-500 text-white hover:bg-yellow-600"
            >
              Retry
            </button>
            {backendStatus?.nextRetryAt && (
              <div className="mt-2 text-xs text-gray-500">
                Next auto-retry at {new Date(backendStatus.nextRetryAt).toLocaleTimeString()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


function LoginWrapper({ initialView = 'login' }) {
  const { socialLoginSettings } = useData();
  return <Login key={JSON.stringify(socialLoginSettings)} initialView={initialView} />;
}

function AppContent() {
  const { user, userType, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Show loading state while checking authentication
  if (loading) {
    return <LoadingSpinner />;
  }

  // If user is not authenticated, show auth routes
  if (!user) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <div className="min-h-screen bg-gray-50">
          {/* Test route for login */}
          <Routes>
            <Route path="/login-test" element={<LoginTest />} />
            <Route path="/auth-test" element={<AuthTest />} />
            <Route path="/auth-test-enhanced" element={<AuthTestEnhanced />} />
            <Route path="/login" element={<LoginWrapper />} />
            <Route path="/forgot-password" element={<LoginWrapper initialView="forgot" />} />
            <Route path="/login-enhanced" element={<EnhancedLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Register />} />
            {/* Test route for environment variables */}
            <Route path="/env-test" element={<EnvTest />} />
            {/* Test route for API communication */}
            <Route path="/api-test" element={<ApiTest />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            {/* Redirect all other routes to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Suspense>
    );
  }

  // If user is authenticated, show main app
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <div className="flex flex-col min-h-[100dvh] bg-gray-50">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <div className="flex flex-1 overflow-hidden">
          <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} userType={userType} />
          <main className="flex-1 p-4 md:p-6 overflow-auto min-h-0">
            <Routes>
              {/* Test routes */}
              <Route path="/login-test" element={<LoginTest />} />
              <Route path="/auth-test" element={<AuthTest />} />
              <Route path="/env-test" element={<EnvTest />} />
              <Route path="/api-test" element={<ApiTest />} />
              <Route path="/socket-test" element={<SocketTest />} />
              <Route path="/datacontext-test" element={<DataContextTest />} />

              {userType === 'admin' ? (
                <>
                  <Route path="/" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin" element={<Navigate to="/" replace />} />
                  <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
                  <Route path="/admin/investments" element={<ProtectedRoute adminOnly><AdminInvestments /></ProtectedRoute>} />
                  <Route path="/admin/plans" element={<ProtectedRoute adminOnly><AdminInvestmentPlansManagement /></ProtectedRoute>} />
                  <Route path="/admin/transactions" element={<ProtectedRoute adminOnly><AdminTransactions /></ProtectedRoute>} />
                  <Route path="/admin/withdrawals" element={<ProtectedRoute adminOnly><AdminWithdrawals /></ProtectedRoute>} />
                  <Route path="/admin/kyc" element={<ProtectedRoute adminOnly><AdminKYC /></ProtectedRoute>} />
                  <Route path="/admin/roi" element={<ProtectedRoute adminOnly><AdminROI /></ProtectedRoute>} />
                  <Route path="/admin/settlements" element={<ProtectedRoute adminOnly><AdminSettlements /></ProtectedRoute>} />
                  <Route path="/admin/bank-details" element={<ProtectedRoute adminOnly><AdminBankDetails /></ProtectedRoute>} />
                  <Route path="/admin/notifications" element={<ProtectedRoute adminOnly><AdminNotifications /></ProtectedRoute>} />
                  <Route path="/admin/referrals" element={<ProtectedRoute adminOnly><AdminReferrals /></ProtectedRoute>} />
                  <Route path="/admin/goldrates" element={<ProtectedRoute adminOnly><AdminGoldRates /></ProtectedRoute>} />
                  <Route path="/admin/payment-gateways" element={<ProtectedRoute adminOnly><PaymentManagement /></ProtectedRoute>} />
                  <Route path="/admin/reports" element={<ProtectedRoute adminOnly><AdminReports /></ProtectedRoute>} />
                  <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
                  <Route path="/admin/profile" element={<ProtectedRoute adminOnly><AdminProfile /></ProtectedRoute>} />
                  {/* Redirect auth routes to dashboard for authenticated admin */}
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="/register" element={<Navigate to="/" replace />} />
                  <Route path="/signup" element={<Navigate to="/" replace />} />
                  <Route path="/forgot-password" element={<Navigate to="/" replace />} />
                </>
              ) : (
                <>
                  <Route path="/" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
                  <Route path="/invest" element={<ProtectedRoute><InvestmentPlans /></ProtectedRoute>} />
                  <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
                  <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                  <Route path="/withdrawal" element={<ProtectedRoute><Withdrawal /></ProtectedRoute>} />
                  <Route path="/transactions" element={<ProtectedRoute><TransactionHistory /></ProtectedRoute>} />
                  <Route path="/bank-details" element={<ProtectedRoute><BankDetails /></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
                  <Route path="/kyc" element={<ProtectedRoute><KYC /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
                  <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
                  {/* Redirect auth routes to dashboard for authenticated user */}
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="/register" element={<Navigate to="/" replace />} />
                  <Route path="/signup" element={<Navigate to="/" replace />} />
                  <Route path="/forgot-password" element={<Navigate to="/" replace />} />
                </>
              )}
            </Routes>
          </main>
        </div>
        {/* Global Toast Container */}
        <ToastContainer />
      </div>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <ScrollToTop />
          <AppContent />
          <Suspense fallback={null}>
            <RealTimeNotifications />
          </Suspense>
          {/* Firebase Phone Auth reCAPTCHA container (invisible) */}
          <div id="recaptcha-container" className="hidden" />
        </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
